import { translate, getLocale } from 'shared/lib/i18n.tsx';

import type { TvQualityProfile } from '../infrastructure/qualityProfile.ts';

import {
  parseSkyRaceFrame, type SkyRaceFrame, type SkyRaceMode, type SkyRacePose,
} from './skyRaceFrame.ts';
import { drawUpperBodyCalibration } from './upperBodyCalibration.ts';

/**
 * Sky Race на ТВ (GEN-18724). Правила и отрисовка — та же страница, что и у телефона
 * (`src/games/sky-race/page`, копия в `dist/client/games/sky-race`), только в iframe на весь экран.
 * Телефон — камера и контроллер: калибруется сам, считает позу и шлёт кадры
 * `{type: 'SkyRaceData', gameState, level, mode, detectionProgress, pose}` ~30 раз/с; ТВ передаёт позу в
 * `grooveSetPose`. Счёт страница отдаёт сообщением `finished` → `onFinished` → `game:state gameover`.
 *
 * Режим и уровень приходят в кадрах, а страница читает их при загрузке — поэтому iframe поднимается по первому
 * кадру (или по `start-game`) и собирается заново, если телефон прислал другой режим или уровень.
 * Пауза, возобновление и рестарт идут полем `gameState` кадров (канал без гарантий доставки: телефон повторяет
 * кадры состояния; повторы здесь безвредны, действие выполняется только при смене фазы).
 */
export type SkyRacePhase = 'lobby' | 'setup' | 'calibration' | 'playing' | 'paused' | 'gameover';

/** Методы страницы (контракт Sky Race, как у Bird Flight). */
interface SkyRacePageApi {
  grooveStart(): void;
  groovePause(): void;
  grooveResume(): void;
  grooveRestart(): void;
  grooveSetPose(pose: SkyRacePose): void;
}

const Z_INDEX = '1';
const PAGE_URL = '/pages/games/sky-race/index.html';
/** Фон iframe до первого кадра страницы — небо долины. */
const SKY_COLOR = '#cfe9f8';
/** Страница должна отозваться `ready` за это время, иначе показываем ошибку. */
const LOAD_TIMEOUT_MS = 15_000;

export class SkyRaceDisplayEngine {
  private phase: SkyRacePhase = 'lobby';

  private host: HTMLDivElement | null = null;

  private frame: HTMLIFrameElement | null = null;

  private statusEl: HTMLDivElement | null = null;

  private calibCanvas: HTMLCanvasElement | null = null;

  private calibRaf = 0;

  /** Заполнение рамки на телефоне, 0…1. */
  private detectionProgress = 0;

  /** Режим и уровень с телефона; до первого кадра — таймер и долина. */
  private mode: SkyRaceMode = 'timed';

  private level = 'valley';

  private pageReady = false;

  /** `start-game` пришёл, `grooveStart()` ещё не вызван (страница не успела загрузиться). */
  private startPending = false;

  private pageStarted = false;

  /** После последнего (ре)старта приходили кадры `STARTED`. */
  private sawStarted = false;

  private loadTimer = 0;

  onFinished?: (stars: number, distance: number) => void;

  constructor(private readonly quality: TvQualityProfile) {}

  getPhase(): SkyRacePhase {
    return this.phase;
  }

  /** `select-game` и рестарт с телефона: калибровка на телефоне, ТВ рисует рамку по её прогрессу. */
  startCalibration(): void {
    this.ensureHost();
    if (this.frame) this.api()?.grooveRestart();
    this.startPending = false;
    this.pageStarted = false;
    this.sawStarted = false;
    this.detectionProgress = 0;
    this.phase = 'calibration';
    this.showStatus(null);
    this.showCalibration(true);
  }

  /** `start-game` с телефона: калибровка пройдена, начинаем забег. */
  startGame(): void {
    this.ensureHost();
    this.ensurePage();
    this.phase = 'playing';
    this.sawStarted = false;
    this.showCalibration(false);
    this.showStatus(null);
    this.startPage();
  }

  // ─── Headless (GEN-18912): ТВ ведёт забег сам, поза — из BodyPoseData, кадров SkyRaceData нет ──────────

  /** Выбор уровня и режима на ТВ: небо на фоне, страница ещё не нужна (фаза не `lobby` — игра уже идёт). */
  beginSetup(): void {
    this.ensureHost();
    this.phase = 'setup';
    this.showCalibration(false);
    this.showStatus(null);
  }

  /**
   * Уровень и режим выбраны: страница собирается под них заранее, пока идёт калибровка
   * (вызывать после `startCalibration`). Тот же выбор после «Ещё раз» — страница остаётся.
   */
  prepareHeadlessPage(mode: SkyRaceMode, level: string): void {
    const changed = mode !== this.mode || level !== this.level;
    this.mode = mode;
    this.level = level;
    if (changed) this.destroyPage();
    this.ensurePage();
  }

  /** Заполнение кольца калибровки, которую считает ТВ. */
  setCalibrationProgress(progress: number): void {
    this.detectionProgress = progress;
  }

  setPose(pose: SkyRacePose): void {
    if (this.phase === 'playing') this.api()?.grooveSetPose(pose);
  }

  pause(text: string): void {
    if (this.phase !== 'playing') return;
    this.api()?.groovePause();
    this.phase = 'paused';
    this.showStatus(text);
  }

  resume(): void {
    if (this.phase !== 'paused') return;
    this.api()?.grooveResume();
    this.phase = 'playing';
    this.showStatus(null);
  }

  /** Итог рисует экран кнопок headless-ТВ — плашка движка не нужна. */
  hideStatus(): void {
    this.showStatus(null);
  }

  onGameFrame(rawFrame: unknown): void {
    const frame = parseSkyRaceFrame(rawFrame);
    if (!frame || this.phase === 'lobby') return;
    this.applySetup(frame);

    switch (frame.gameState) {
    case 'PREPARING':
      // Рестарт с телефона (после паузы или итога) — снова калибровка. Запоздавший кадр подготовки сразу после
      // `start-game`, до первого кадра игры, рестартом не считаем.
      if (this.phase === 'playing' && !this.sawStarted) return;
      if (this.phase !== 'calibration') this.startCalibration();
      if (frame.detectionProgress !== null) this.detectionProgress = frame.detectionProgress;
      break;
    case 'PAUSED':
      if (this.phase !== 'playing') return;
      this.api()?.groovePause();
      this.phase = 'paused';
      this.showStatus(translate('game.pause'));
      break;
    case 'STARTED':
      if (this.phase === 'playing' || this.phase === 'paused') this.sawStarted = true;
      if (this.phase === 'paused') {
        this.api()?.grooveResume();
        this.phase = 'playing';
        this.showStatus(null);
      }
      if (this.phase === 'playing' && frame.pose) this.api()?.grooveSetPose(frame.pose);
      break;
    default:
      break;
    }
  }

  reset(): void {
    this.phase = 'lobby';
    this.showCalibration(false);
    this.destroyPage();
    this.host?.remove();
    this.host = null;
    this.statusEl = null;
    this.calibCanvas = null;
  }

  /** Контейнер, статус и канва калибровки — дёшево, без страницы. */
  private ensureHost(): void {
    if (this.host) return;
    const host = document.createElement('div');
    host.style.cssText = `position:fixed;inset:0;z-index:${Z_INDEX};background:${SKY_COLOR};`;
    document.body.appendChild(host);

    const calib = document.createElement('canvas');
    calib.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;z-index:2;display:none;';
    host.appendChild(calib);

    const status = document.createElement('div');
    status.style.cssText = 'position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);z-index:3;'
      + 'padding:24px 40px;border-radius:24px;background:rgba(20,14,50,.75);color:#fff;'
      + 'font:800 40px -apple-system,system-ui,sans-serif;text-align:center;display:none;';
    host.appendChild(status);

    this.host = host;
    this.calibCanvas = calib;
    this.statusEl = status;
  }

  /** Страница под выбранные режим и уровень; если она уже есть — остаётся как есть. */
  private ensurePage(): void {
    if (this.frame || !this.host) return;
    const frame = document.createElement('iframe');
    const lang = getLocale() === 'kk' ? 'kz' : getLocale();
    const query = new URLSearchParams({
      platform: 'tv', mode: this.mode, level: this.level, lang,
    });
    // Слабому ТВ страница рисует в родном размере (как у Bird Flight).
    if (this.quality !== 'high') query.set('quality', 'low');
    frame.src = `${PAGE_URL}?${query.toString()}`;
    frame.setAttribute('scrolling', 'no');
    frame.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;border:0;z-index:1;'
      + `background:${SKY_COLOR};`;
    this.host.appendChild(frame);
    this.frame = frame;
    this.pageReady = false;
    this.pageStarted = false;
    window.addEventListener('message', this.onMessage);
    window.clearTimeout(this.loadTimer);
    this.loadTimer = window.setTimeout(() => {
      if (!this.pageReady) this.showStatus(translate('game.chef_load_failed'));
    }, LOAD_TIMEOUT_MS);
  }

  private destroyPage(): void {
    window.clearTimeout(this.loadTimer);
    window.removeEventListener('message', this.onMessage);
    this.frame?.remove();
    this.frame = null;
    this.pageReady = false;
    this.startPending = false;
    this.pageStarted = false;
  }

  /**
   * Телефон прислал режим или уровень: страница читает их при загрузке, поэтому поднимаем её (или пересобираем
   * под другие). Фаза остаётся: если забег уже идёт, новый стартует сразу.
   */
  private applySetup(frame: SkyRaceFrame): void {
    const mode = frame.mode ?? this.mode;
    const level = frame.level ?? this.level;
    const changed = mode !== this.mode || level !== this.level;
    this.mode = mode;
    this.level = level;
    if (this.phase === 'lobby') return;
    if (!this.frame) {
      this.ensurePage();
      return;
    }
    if (!changed) return;
    // Кадр подготовки — телефон в калибровке: новый забег начнётся по `start-game`, а не сам.
    const wasPlaying = (this.phase === 'playing' || this.phase === 'paused') && frame.gameState !== 'PREPARING';
    this.destroyPage();
    this.ensurePage();
    if (wasPlaying) {
      this.phase = 'playing';
      this.showStatus(null);
      this.startPending = true;
    }
  }

  private readonly onMessage = (event: MessageEvent): void => {
    if (event.origin !== window.location.origin || event.source !== this.frame?.contentWindow) return;
    const { data } = event as { data: unknown };
    const message = data && typeof data === 'object' ? (data as Record<string, unknown>).groove : undefined;
    if (!message || typeof message !== 'object') return;
    const {
      type, stars, distance, message: text,
    } = message as Record<string, unknown>;

    if (type === 'ready') {
      this.pageReady = true;
      window.clearTimeout(this.loadTimer);
      // Страница ответила после таймаута — плашка «не удалось загрузить» уже на экране.
      if (this.phase !== 'paused' && this.phase !== 'gameover') this.showStatus(null);
      if (this.startPending) this.startPage();
    } else if (type === 'finished') {
      const count = typeof stars === 'number' && Number.isFinite(stars) ? stars : 0;
      const meters = typeof distance === 'number' && Number.isFinite(distance) ? Math.floor(distance) : 0;
      this.phase = 'gameover';
      // Страница оставляет мир на экране — итог рисуем поверх.
      this.showStatus(translate('game.sky_race_over', {
        stars: count,
        meters: translate('game.meters', { n: meters }),
      }));
      this.onFinished?.(count, meters);
    } else if (type === 'error') {
      // eslint-disable-next-line no-console
      console.warn('[SkyRace] page error', text);
      // Страница, которая сломалась до `ready`, сама не оживёт: говорим, а не ждём таймаута.
      if (!this.pageReady) this.showStatus(translate('game.chef_load_failed'));
    }
  };

  private startPage(): void {
    if (this.pageStarted) return;
    const page = this.api();
    if (!page) {
      this.startPending = true;
      return;
    }
    this.startPending = false;
    this.pageStarted = true;
    page.grooveStart();
  }

  /** Окно страницы, когда она загрузилась и сказала `ready`; иначе `null`. */
  private api(): SkyRacePageApi | null {
    if (!this.pageReady) return null;
    return (this.frame?.contentWindow as unknown as SkyRacePageApi | null | undefined) ?? null;
  }

  private showStatus(text: string | null): void {
    if (!this.statusEl) return;
    this.statusEl.textContent = text ?? '';
    this.statusEl.style.display = text ? 'block' : 'none';
  }

  // ─── Калибровка ───────────────────────────────────────────────────────────

  /** Экран «встаньте в зону», как у Bird Flight: силуэт по пояс — самолёту нужны только плечи и запястья. */
  private showCalibration(visible: boolean): void {
    cancelAnimationFrame(this.calibRaf);
    this.calibRaf = 0;
    if (!this.calibCanvas) return;
    this.calibCanvas.style.display = visible ? 'block' : 'none';
    if (!visible) return;
    const tick = (): void => {
      if (this.phase !== 'calibration') return;
      this.drawCalibration();
      this.calibRaf = requestAnimationFrame(tick);
    };
    tick();
  }

  private drawCalibration(): void {
    const canvas = this.calibCanvas;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const W = window.innerWidth;
    const H = window.innerHeight;
    if (canvas.width !== W) canvas.width = W;
    if (canvas.height !== H) canvas.height = H;

    drawUpperBodyCalibration(ctx, W, H, this.detectionProgress);
  }
}
